import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { Modal, Button, Dropdown, Form, Select, Input, Checkbox, Badge, Spin, Divider, App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { SaveOutlined, CheckOutlined, DownOutlined } from '@ant-design/icons';
import Draggable from 'react-draggable';

import { getTinyMCEEditorTheme, isGuid, GuidEmpty, renderNoDataOrSpinner } from '../../../shared/utils';
import { NoteOwnerTypes, stickyNoteColor } from '../../../shared/constants/enums';

import ReminderComponent from '../note/reminder';

import Filter from '../../../shared/components/filter';

import * as actions from './../store/actions';
import * as smsActions from '../../smses/store/actions';

import * as sharedActions from '../../../shared/store/actions';

import { RootState } from '../../../store';

import { filterTypes } from '../../../shared/constants/filterType';
import { siteUrls } from '../../../shared/constants/siteUrls';

import { isEmpty, isString } from 'lodash';

import {
  InsuredPhone,
  KeyValuePair,
  NotesRequestBase,
  NoteType,
  Policy
} from '../../../shared/models';

import './../styles.less';
import { MessageControlParameters } from '../../smses/models';

// Global timeout variables
let insuredFieldTimeout: ReturnType<typeof setTimeout> | null;
let typeFieldTimeout: ReturnType<typeof setTimeout> | null;

// Type definitions
interface DraggableBounds {
  left: number;
  top: number;
  bottom: number;
  right: number;
}

export type NoteModalProps = {
  visible: boolean;
  isQuickNote?: boolean;
  close: () => void;
  refresh?: () => void;
  baseRequestParams: NotesRequestBase;
  isSuperUserNotes?: boolean;
  isMomentumEnabled?: boolean;
  fileId?: string;
};

export const NoteModal: React.FC<NoteModalProps> = ({
  visible,
  isQuickNote = false,
  close,
  refresh,
  baseRequestParams,
  isSuperUserNotes = false,
  isMomentumEnabled = false,
  fileId,
}) => {
  const mceEditorId = `noteSubjectEditor${isQuickNote ? '_quick_note' : ''}`;

  const { modal, notification } = App.useApp();

  //#region Draggable Feature
  const [draggableDisabled, setDraggableDisabled] = useState<boolean>(false);
  const [draggableBounds, setDraggableBounds] = useState<DraggableBounds>({ left: 0, top: 0, bottom: 0, right: 0 });
  const draggleRef = useRef<HTMLDivElement>(null);

  const onStart = (_event: any, uiData: any): void => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }

    const bounds = {
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    };

    setDraggableBounds(bounds);
  };
  //#endregion Draggable Feature

  //#region State
  const [isReminderApplied, setReminderApplied] = useState<boolean>(false);
  const [isSendAsSmsApplied, setSendAsSms] = useState<boolean>(false);
  const [isStickyNoteApplied, setStickyNote] = useState<boolean>(false);
  const [newType, setNewType] = useState<string>('');
  const [editorLoading, setEditorLoading] = useState<boolean>(true);
  const [isSubjectValid, setSubjectValidity] = useState<boolean>(true);
  const [isNewTypeValid, setNewTypeValidity] = useState<boolean>(true);
  //#endregion

  //#region Props


  const {
    insureds,
    insuredsLoading,
    policies,
    policiesLoading,
    tasks,
    tasksLoading,
    claims,
    claimsLoading,
    noteTypes,
    noteTypesLoading,
    contactPhones,
    contactPhonesLoading,
    noteDispositions,
    noteDispositionsLoading,
  } = useSelector((state: RootState) => state.noteTypes);

  const createNoteLoading = useSelector((state: RootState) => state.notes.createNoteLoading);

  const { messageControlParameters, messageControlParametersLoading } = useSelector((state: RootState) => state.smses);
  //#endregion

  const dispatch = useDispatch();

  const [reminderForm] = Form.useForm();
  const [noteForm] = Form.useForm();
  const [additionalNoteDataForm] = Form.useForm();

  //#region React Lifecycle Hooks
  useEffect(() => {
    if (visible) {
      if (isQuickNote) {
        if (baseRequestParams.ownerType !== NoteOwnerTypes.Insured) {
          dispatch(actions.getInsuredsProspects({ contactId: baseRequestParams.insuredId, pageSize: 15, pageNumber: 1 }) as any);
        }

        setReminderApplied(true);
      }

      dispatch(actions.getNoteTypes() as any);
      dispatch(actions.getNoteDispositions(null) as any);
    }
  }, [visible]);

  useEffect(() => {
    const editor = (window as any).tinymce.get(mceEditorId);

    if (!visible && editor) {
      try {
        editor.remove();
      } catch (e) {
        console.log(e);
      }
    } else if (visible) {
      setEditorLoading(true);

      console.log('MCE control init start');

      setTimeout(() => {
        (window as any).tinymce.init({
          selector: `div#${mceEditorId}`,
          auto_focus: mceEditorId,
          browser_spellcheck: true,
          max_height: 500,
          min_height: 350,
          ...getTinyMCEEditorTheme(),
          init_instance_callback: (editor: any) => {
            console.log('MCE control initialized');

            setEditorLoading(false);

            editor.on('SetContent', (e: any) => {
              handleSubjectChange(editor.getContent({ format: 'text' }));
            });

            editor.on('input', (e: any) => {
              handleSubjectChange(editor.getContent({ format: 'text' }));
            });
          },
          menubar: false,
          statusbar: false,
          forced_root_block: false,
          nc_templates_fetch_template_items: async (editorContent: string) => {
            return (await dispatch(actions.getNoteTemplates() as any)).map((item: any) => {
              return {
                text: item.name,
                value: `${editorContent !== '' ? '<br/>' : ''}${item.body}`,
              };
            });
          },
          nc_templates_on_before_item_apply: (messageBody: string) => {
            return new Promise((resolve, reject) => {
              if (
                messageBody.indexOf('{Insured.') !== -1 ||
                messageBody.indexOf('{InsuranceAgency.') !== -1 ||
                messageBody.indexOf('{Agent.') !== -1 ||
                messageBody.indexOf('{CSR.') !== -1 ||
                messageBody.indexOf('{Policy.') !== -1 ||
                messageBody.indexOf('{Util.') !== -1
              ) {
                modal.confirm({
                  title: 'Replace Placeholders',
                  icon: <ExclamationCircleOutlined />,
                  content: (
                    <div>The selected template contains placeholders. Do you want to replace the possible ones? (This may take a few seconds.)</div>
                  ),
                  okText: 'Yes',
                  cancelText: 'No',
                  onOk: async () => {
                    let insuredId = additionalNoteDataForm.getFieldValue('insuredId');

                    if (!isGuid(insuredId)) {
                      insuredId = baseRequestParams.insuredId;
                    }

                    resolve(await dispatch(sharedActions.replaceMessagePlaceholders({ message: messageBody, truckingCompanyId: insuredId, policyId: baseRequestParams.policyId }) as any));
                  },
                  onCancel: () => {
                    resolve(messageBody);
                  },
                } as any);
              } else {
                resolve(messageBody);
              }
            });
          },
          plugins: 'nc-templates lists advlist code nc-speech-recognition',
          toolbar_mode: 'sliding',
          toolbar:
            'nc-speech-recognition | undo redo | nc-templates fontselect fontsizeselect | bold italic underline strikethrough | forecolor backcolor |\
             alignleft aligncenter alignright |\
             outdent indent | bullist numlist | code',
        });
      }, 100);
    }
  }, [visible]);
  //#endregion

  const validateMessages = {
    required: '${label} is required.',
    types: {
      number: '${label} is not a valid number.',
    },
  };

  const handleSave = (addTask = false): void => {
    // give DatePicker a tick to finish onChange
    setTimeout(() => {
      const editor = (window as any).tinymce.get(mceEditorId);

      if (editor) {
        const subject = editor.save();

        Promise.all([noteForm.validateFields(), reminderForm.validateFields(), additionalNoteDataForm.validateFields()])
          .then((values) => {
            let isNotEmpty = subject !== undefined && subject !== null && subject.replace(/&nbsp;/g, ' ').trim().length > 0;
            setSubjectValidity(isNotEmpty);

            if (isNotEmpty) {
              let formData = { ...baseRequestParams, ...values[0], ...values[1], ...values[2] }; // merge of 3 forms values
              formData.subject = `<div>${subject}</div>`;

              if (formData.policyId || formData.policyIds) {
                let policiesArray: string[] = [];

                if (formData.policyId) {
                  policiesArray = [formData.policyId];
                }

                if (formData.policyIds && formData.policyIds.length) {
                  policiesArray = [...policiesArray, ...formData.policyIds];
                  if (!formData.policyId) {
                    formData.policyId = formData.policyIds[0];
                  }
                }

                if (policiesArray.length) {
                  formData.policyIds = policiesArray;
                }
              }

              if (formData.reminderSendToUsers && formData.reminderSendToUsers.length) {
                const updatedReminderSendToUsers = formData.reminderSendToUsers.map((x: string) => {
                  const array = x.split('|');

                  if (array.length > 1) {
                    return `${array[0]}|${array[1]}`;
                  } else if (array.length === 1) {
                    return `${array[0]}|`;
                  } else {
                    return '';
                  }
                });

                formData.reminderSendToUsers = updatedReminderSendToUsers;
              }

              if (isQuickNote) {
                if (formData.insuredId) {
                  formData.ownerType = NoteOwnerTypes.Insured;
                }

                formData.isQuickNote = true;
              }

              formData.messageControlParameters = messageControlParameters;
              formData.fileId = isGuid(fileId) ? fileId : null;

              dispatch(actions.createNote(formData) as any).then((res: any) => {
                notify();

                if (addTask && res && isGuid(res)) {
                  const taskBlankUrl = siteUrls.getTaskCreateUrl(
                    isMomentumEnabled,
                    res,
                    formData.insuredId || GuidEmpty,
                    formData.policyIds || [],
                    true,
                    null
                  );

                  window.open(taskBlankUrl, '_blank', 'noopener');
                }

                if (isStickyNoteApplied) {
                  if (typeof (document as any).loadStickyNotes === 'function') {
                    (document as any).loadStickyNotes();
                  }

                  document.dispatchEvent(
                    new CustomEvent('NCM_STICKY_NOTE_CREATED', {
                      detail: {},
                    }),
                  );
                }

                clearForm();

                if (typeof refresh === 'function') {
                  refresh();
                }

                close();
              });
            }
          })
          .catch(() => setSubjectValidity(subject !== ''));
      }
    }, 0);
  };

  const notify = (): void => {
    notification.success({
      message: 'Success',
      top: 55,
      duration: 10,
      description: 'The note was successfully saved.',
    } as any);
  };

  const handleSaveNewType = (): void => {
    if (!!newType) {
      dispatch(actions.createNoteType({ name: newType }) as any);
    } else {
      setNewTypeValidity(false);
    }
  };

  const handleNewTypeChange = (value: string): void => {
    setNewType(value);
    setNewTypeValidity(!!value);
  };

  const handleClose = (): void => {
    modal.confirm({
      title: 'Are you sure you want to discard this note?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        clearForm();
        close();

        return false;
      },
    });
  };

  const getContactPhonesParams = (): { contactId: string | null | undefined; noteOwnerType: number | undefined } => {
    switch (baseRequestParams.ownerType) {
      case NoteOwnerTypes.Insured:
      case NoteOwnerTypes.Policy:
      case NoteOwnerTypes.Task:
      case NoteOwnerTypes.Opportunity:
      case NoteOwnerTypes.Claim:
      case NoteOwnerTypes.ServiceRequest:
        return {
          contactId: additionalNoteDataForm.getFieldValue('insuredId') || baseRequestParams.insuredId,
          noteOwnerType: baseRequestParams.ownerType,
        };
      case NoteOwnerTypes.Agent:
        return {
          contactId: baseRequestParams.agentId,
          noteOwnerType: baseRequestParams.ownerType,
        };
      case NoteOwnerTypes.Agency:
        return {
          contactId: baseRequestParams.agencyId,
          noteOwnerType: baseRequestParams.ownerType,
        };

      case NoteOwnerTypes.SuperUserAgencyNotes:
      default:
        return {
          contactId: null,
          noteOwnerType: baseRequestParams.ownerType,
        };
    }
  };

  const handleSendAsSmsChange = (checked: boolean): void => {
    if (checked) {
      dispatch(smsActions.getMessageControlParameters() as any)
        .then((resMessageControlParameters: MessageControlParameters) => {
          if (
            resMessageControlParameters &&
            isString(resMessageControlParameters.errorMessage) &&
            !isEmpty(resMessageControlParameters.errorMessage)
          ) {
            modal.error({
              title: resMessageControlParameters.errorMessage,
            });

            resMessageControlParameters.errorMessage = undefined;

            additionalNoteDataForm.resetFields(['sendSms']);
            setSendAsSms(false);
          } else {
            setSendAsSms(checked);

            const contactInfo = getContactPhonesParams();
            if (checked && contactInfo) {
              dispatch(actions.getContactPhones(contactInfo) as any);
            }
          }
        })
        .catch((error: any) => {
          additionalNoteDataForm.resetFields(['sendSms']);
          setSendAsSms(false);
        });
    } else {
      additionalNoteDataForm.resetFields(['sendSms']);
      additionalNoteDataForm.resetFields(['insuredsPhone']);
      setSendAsSms(false);
    }
  };

  const handleInsuredsPhoneChange = (value: string): void => {
    dispatch(
      smsActions.checkExistedChat({
        toNumber: value.replace('+1', '').replace(/\D/g, ''),
        smsServiceType: messageControlParameters?.smsServiceType,
        ownerAccountUserId: messageControlParameters?.ownerAccountUserId,
        myPhoneNumber: messageControlParameters?.myNumber,
      }) as any)
      .then((resCheckExistedChat: any) => { })
      .catch((error: any) => {
        additionalNoteDataForm.resetFields(['insuredsPhone']);
      });
  };

  const clearForm = (): void => {
    const editor = (window as any).tinymce.get(mceEditorId);

    if (editor) {
      editor.setContent('');
    }

    setReminderApplied(false);
    setSendAsSms(false);
    setStickyNote(false);
    setNewType('');
    setEditorLoading(true);
    setSubjectValidity(true);
    setNewTypeValidity(true);

    noteForm.resetFields();
    reminderForm.resetFields();
    additionalNoteDataForm.resetFields();
  };

  const handleReminderChange = (checked: boolean): void => setReminderApplied(checked);

  const handleSubjectChange = (newValueWithoutHTML: string): void => {
    setSubjectValidity(newValueWithoutHTML !== '');
  };

  const handleDropdownVisibilityChange = (opened: boolean): void => {
    if (!opened) setNewTypeValidity(true);
  };

  const badgeColorsOpts: Record<string, string> = {
    1: 'orange',
    2: 'yellow',
    3: 'green',
    4: 'blue',
    5: 'geekblue',
    6: 'purple',
    7: 'pink',
  };

  const formItemLayout = {
    labelCol: { span: 7 },
    wrapperCol: { span: 17 },
  };

  const handleSearchInsured = (searchString: string): void => {
    if (insuredFieldTimeout) {
      clearTimeout(insuredFieldTimeout);
      insuredFieldTimeout = null;
    }

    insuredFieldTimeout = setTimeout(
      () =>
        dispatch(
          actions.getInsuredsProspects({
            contactId: additionalNoteDataForm.getFieldValue('insuredId') || baseRequestParams.insuredId,
            pageSize: 15,
            pageNumber: 1,
            searchString,
          }) as any),
      500,
    );
  };

  const handleInsuredChange = (value: string): void => {
    dispatch(actions.getInsuredsProspects({ contactId: value, pageSize: 15, pageNumber: 1 }) as any);
    const sendSms = additionalNoteDataForm.getFieldValue('sendSms');

    if (sendSms) {
      const contactInfo = getContactPhonesParams();
      dispatch(actions.getContactPhones(contactInfo) as any);
    }
  };

  const handleSearchType = (searchString: string): void => {
    if (typeFieldTimeout) {
      clearTimeout(typeFieldTimeout);
      typeFieldTimeout = null;
    }

    typeFieldTimeout = setTimeout(() => dispatch(actions.getNoteTypes(searchString) as any), 500);
  };

  const footerSaveMenuItems = [
    {
      label: 'Save & Add Task',
      onClick: () => handleSave(true),
      key: 'save_note_and_add_a_task',
      icon: <CheckOutlined />,
    },
  ];

  return (
    <Modal
      title={
        <div
          style={{
            width: '100%',
            cursor: 'move',
          }}
          onMouseOver={() => {
            if (draggableDisabled) {
              setDraggableDisabled(false);
            }
          }}
          onMouseOut={() => {
            setDraggableDisabled(true);
          }}
        >
          Create new note
        </div>
      }
      centered
      open={visible}
      onOk={() => handleSave(false)}
      onCancel={() => handleClose()}
      width={isSuperUserNotes ? 750 : 1350}
      maskClosable={false}
      styles={{ body: { padding: 0 } }}
      destroyOnClose={true}
      footer={
        <div style={{ display: 'flex', justifyContent: 'left', paddingLeft: '9px' }}>
          <Button key="back" onClick={handleClose}>
            Close
          </Button>

          <Dropdown.Button
            className="save-note-dropdown-button"
            style={{ paddingLeft: '10px', borderColor: '#279CB3', color: '#279CB3' }}
            menu={{ items: footerSaveMenuItems }}
            key="button"
            type="default"
            icon={<DownOutlined />}
            loading={createNoteLoading}
            disabled={createNoteLoading}
            getPopupContainer={() => document.getElementById('new-note-modal-11022') as HTMLElement}
            buttonsRender={([leftButton, rightButton]) => [
              <Button
                key="button"
                type="primary"
                ghost
                htmlType="button"
                icon={<CheckOutlined />}
                onClick={() => handleSave(false)}
                loading={createNoteLoading}
                disabled={createNoteLoading}
              >
                Save
              </Button>,
              <Button key="button-menu" type="primary" ghost htmlType="button" icon={<DownOutlined />}></Button>,
            ]}
          />
        </div>
      }
      confirmLoading={createNoteLoading}
      okButtonProps={{ disabled: createNoteLoading }}
      modalRender={(modal) => (
        <Draggable disabled={draggableDisabled} nodeRef={draggleRef} bounds={draggableBounds} onStart={(event, uiData) => onStart(event, uiData)}>
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
    >
      <div
        id="new-note-modal-11022"
        style={{
          maxHeight: '750px',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '10px 25px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ width: isSuperUserNotes ? '100%' : '63%' }}>
            <Form form={noteForm} style={{ width: '100%' }} layout="vertical" size="small" validateMessages={validateMessages} labelAlign="left">
              <Form.Item label="Subject" className={!isSubjectValid ? 'subject-form-item-errored' : ''}>
                <Spin spinning={editorLoading}>
                  <div id={mceEditorId}></div>
                  {!isSubjectValid ? <p style={{ color: 'red', marginTop: '3px', marginBottom: 0 }}>This field is required!</p> : null}
                </Spin>
              </Form.Item>
            </Form>
          </div>

          {!isSuperUserNotes ? (
            <div style={{ width: '35%' }}>
              <Form
                {...formItemLayout}
                form={additionalNoteDataForm}
                style={{ width: '100%' }}
                layout="horizontal"
                size="small"
                validateMessages={validateMessages}
                labelAlign="left"
              >
                <Form.Item label="Type" name={['noteTypeId']}>
                  <Select
                    allowClear
                    showSearch
                    onDropdownVisibleChange={handleDropdownVisibilityChange}
                    loading={noteTypesLoading}
                    filterOption={false}
                    onSearch={handleSearchType}
                    getPopupContainer={() => document.getElementById('new-note-modal-11022') as HTMLElement}
                    placeholder="Please choose a type"
                    notFoundContent={renderNoDataOrSpinner(noteTypesLoading)}
                    dropdownRender={(menu) => (
                      <div>
                        {menu}
                        <Divider style={{ margin: '4px 0' }} />
                        <div style={{ display: 'flex', flexWrap: 'nowrap', padding: '4px 8px 4px 8px' }}>
                          <Input style={{ flex: 'auto' }} value={newType} onChange={(e) => handleNewTypeChange(e.target.value)} size="small" />
                          <a style={{ flex: 'none', padding: '0 15px', display: 'block', cursor: 'pointer' }} onClick={handleSaveNewType}>
                            <SaveOutlined /> Add item
                          </a>
                        </div>
                        {isNewTypeValid ? null : <p style={{ color: 'red', marginBottom: 0, padding: '0 10px' }}>This field is required!</p>}
                      </div>
                    )}
                  >
                    {noteTypes && noteTypes.length
                      ? noteTypes.map((type: NoteType) => (
                        <Select.Option value={type.id} key={type.id} title={type.name}>
                          {type.name}
                        </Select.Option>
                      ))
                      : null}
                  </Select>
                </Form.Item>

                <Form.Item label="Disposition" name={['dispositionId']}>
                  <Select
                    allowClear
                    showSearch
                    loading={noteDispositionsLoading}
                    filterOption={(input, option) => (option!.title as string).toLowerCase().includes(input.toLowerCase())}
                    getPopupContainer={() => document.getElementById('new-note-modal-11022') as HTMLElement}
                    placeholder="Please choose a disposition"
                    notFoundContent={renderNoDataOrSpinner(noteDispositionsLoading)}
                  >
                    {noteDispositions && noteDispositions.dataList.length
                      ? noteDispositions.dataList.map((disposition: KeyValuePair) => (
                        <Select.Option value={disposition.key} key={disposition.key} title={disposition.value}>
                          {disposition.value}
                        </Select.Option>
                      ))
                      : null}
                  </Select>
                </Form.Item>

                {baseRequestParams.ownerType !== NoteOwnerTypes.Insured && isQuickNote ? (
                  <Form.Item
                    label="Insured/Prospect"
                    name={['insuredId']}
                    initialValue={baseRequestParams.insuredId ? baseRequestParams.insuredId : null}
                  >
                    <Select
                      allowClear
                      showSearch
                      loading={insuredsLoading}
                      filterOption={false}
                      getPopupContainer={() => document.getElementById('new-note-modal-11022') as HTMLElement}
                      placeholder="Type and/or choose insured/prospect"
                      onSearch={handleSearchInsured}
                      notFoundContent={renderNoDataOrSpinner(insuredsLoading)}
                      onChange={handleInsuredChange}
                    >
                      {insureds && insureds.dataList.length
                        ? insureds.dataList.map((insured: KeyValuePair) => (
                          <Select.Option value={insured.key} key={insured.key} title={insured.value}>
                            {insured.value}
                          </Select.Option>
                        ))
                        : null}
                    </Select>
                  </Form.Item>
                ) : null}

                {baseRequestParams.ownerType !== NoteOwnerTypes.Policy && !isQuickNote ? (
                  <Filter
                    filterType={filterTypes.dropdownWithServerData}
                    name={'policyIds'}
                    label={'Policy'}
                    mode="multiple"
                    placeholder="Type and/or choose policy"
                    data={policies as any}
                    dataLoading={policiesLoading}
                    dataSearchAction={(searchString?: string) => actions.getPolicies(baseRequestParams.insuredId, searchString)}
                    keyPropertyName={'id'}
                    textPropertyName={(item: Policy) => {
                      return `${item.number} (${dayjs(item.effectiveDate).format('MM/DD/YYYY')}, ${item.policyLinesOfBussinessString})`;
                    }}
                    titlePropertyName={(item: Policy) => {
                      return `${item.number} (${dayjs(item.effectiveDate).format('MM/DD/YYYY')}, ${item.policyLinesOfBussinessString})`;
                    }}
                  />
                ) : null}

                {baseRequestParams.ownerType !== NoteOwnerTypes.Task && !isQuickNote ? (
                  <Filter
                    filterType={filterTypes.dropdownWithServerData}
                    name={'taskId'}
                    label={'Task'}
                    mode="single"
                    placeholder="Type and/or choose task"
                    data={tasks as any}
                    dataLoading={tasksLoading}
                    dataSearchAction={(searchString?: string) => actions.getTasks(baseRequestParams.insuredId, searchString)}
                    keyPropertyName={'id'}
                    textPropertyName={'ticketTitle'}
                    titlePropertyName={'ticketTitle'}
                  />
                ) : null}

                {baseRequestParams.ownerType !== NoteOwnerTypes.Claim && !isQuickNote ? (
                  <Filter
                    filterType={filterTypes.dropdownWithServerData}
                    name={'claimId'}
                    label={'Claim'}
                    mode="single"
                    placeholder="Type and/or choose claim"
                    data={claims as any}
                    dataLoading={claimsLoading}
                    dataSearchAction={(searchString?: string) => actions.getClaims(baseRequestParams.insuredId, searchString)}
                    keyPropertyName={'id'}
                    textPropertyName={'claimNumber'}
                    titlePropertyName={'claimNumber'}
                  />
                ) : null}

                <div style={{ display: 'flex' }}>
                  {/* todo: discuss if this 2 field needed to be hidden if owner type NOT insured */}
                  <Form.Item name={['sendSms']} valuePropName="checked" initialValue={false} style={{ width: '170px' }}>
                    <Checkbox className="nc-notes-checkbox" onChange={(e) => handleSendAsSmsChange(e.target.checked)}>
                      <Spin spinning={messageControlParametersLoading === true}>Send as SMS</Spin>
                    </Checkbox>
                  </Form.Item>

                  {isSendAsSmsApplied ? (
                    <Form.Item
                      name={['insuredsPhone']}
                      initialValue={null}
                      rules={[{ required: true, message: 'This field is required.' }]}
                      wrapperCol={{ span: 24 }}
                      style={{ minWidth: '285px', maxWidth: '285px', justifyContent: 'flex-end' }}
                    >
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => (option!.title as string).toLowerCase().includes(input.toLowerCase())}
                        loading={contactPhonesLoading}
                        getPopupContainer={() => document.getElementById('new-note-modal-11022') as HTMLElement}
                        placeholder="Type and/or choose insured's phone"
                        notFoundContent={renderNoDataOrSpinner(contactPhonesLoading)}
                        onChange={handleInsuredsPhoneChange}
                      >
                        {contactPhones && contactPhones.length
                          ? contactPhones.map((option: InsuredPhone) => (
                            <Select.Option
                              value={option.text}
                              key={option.value}
                              title={option.typeString + ' (' + option.text + ') - ' + option.display}
                            >
                              <strong>
                                {option.typeString} ({option.text}) - {option.display}
                              </strong>
                            </Select.Option>
                          ))
                          : null}
                      </Select>
                    </Form.Item>
                  ) : null}
                </div>

                <div style={{ display: 'flex' }}>
                  <Form.Item
                    name={['isStickyNoteApplied']}
                    valuePropName="checked"
                    initialValue={false}
                    style={{ width: '180px' }}
                  >
                    <Checkbox className="nc-notes-checkbox" onChange={(e) => setStickyNote(e.target.checked)}>Is Sticky Note</Checkbox>
                  </Form.Item>

                  {isStickyNoteApplied ? (
                    <Form.Item
                      name={['stickyNoteColor']}
                      initialValue={null}
                      rules={[{ required: true, message: 'This field is required.' }]}
                      wrapperCol={{ span: 24 }}
                      style={{ width: '285px', justifyContent: 'flex-end' }}
                    >
                      <Select
                        allowClear
                        getPopupContainer={() => document.getElementById('new-note-modal-11022') as HTMLElement}
                        placeholder="Choose background color"
                      >
                        {stickyNoteColor.map((item: any) => (
                          <Select.Option key={item.key} value={item.key}>
                            <Badge size="default" color={badgeColorsOpts[item.key]} /> {item.value}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : null}
                </div>
              </Form>

              <ReminderComponent
                form={reminderForm}
                isReminderApplied={isReminderApplied}
                changeReminder={handleReminderChange}
              />
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};
